import { useEffect } from 'react';

/**
 * useMediaSession
 *
 * Publishes a "now playing" entry to the OS media session while a
 * session is active so:
 *   - the iOS / Android lock screen shows the current exercise and
 *     set / total alongside pause / play controls;
 *   - AirPods, Apple Watch, car Bluetooth and any BLE headset's
 *     play / pause button toggles the session without needing to
 *     wake the phone.
 *
 * Implementation notes:
 *   - navigator.mediaSession is only populated while an <audio> or
 *     <video> element is actively playing on iOS Safari, so we keep
 *     a hidden silent looped audio element alive for the duration
 *     of the active session. Chrome, Safari 15+ on macOS, and
 *     Android Chrome all accept a MediaSession without a playing
 *     element, but iOS needs the audio hack. The audio element plays
 *     a 0.1 s 0 Hz buffer so no sound is produced.
 *   - On unmount the audio is paused, src revoked, and the
 *     mediaSession is cleared so no stale "Be Bold" entry lingers
 *     in Control Center after the user leaves the session.
 *   - Safe no-op in environments without navigator.mediaSession or
 *     without the AudioContext API (old browsers, SSR, jsdom).
 */
export default function useMediaSession({
  active,
  title,
  artist = 'Be Bold',
  album,
  artworkUrl = '/logo.png',
  onPlay,
  onPause,
  onStop,
  onNextTrack,
}) {
  useEffect(() => {
    if (!active) return undefined;
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return undefined;

    let silentAudio = null;
    let silentUrl = null;

    // iOS Safari clears navigator.mediaSession metadata unless a media
    // element is actively producing output. A silent WAV buffer looped
    // via <audio> keeps the OS treating us as "playing," which unlocks
    // the lock screen metadata + play/pause controls.
    try {
      silentUrl = makeSilentWavUrl();
      silentAudio = document.createElement('audio');
      silentAudio.src = silentUrl;
      silentAudio.loop = true;
      silentAudio.muted = false;
      silentAudio.volume = 0.0001;
      silentAudio.preload = 'auto';
      silentAudio.playsInline = true;
      // Autoplay restrictions: this hook only activates after the
      // user has already tapped Begin, so there is a valid user
      // gesture in the stack and .play() resolves. If it rejects
      // (e.g. user has disabled autoplay at the OS level), we skip
      // the metadata publish rather than crash.
      silentAudio.play().catch(() => {});
    } catch { /* silent audio unsupported, fall through */ }

    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: title || 'Be Bold session',
        artist,
        album: album || 'Pelvic floor training',
        artwork: [
          { src: artworkUrl, sizes: '96x96',  type: 'image/png' },
          { src: artworkUrl, sizes: '192x192', type: 'image/png' },
          { src: artworkUrl, sizes: '512x512', type: 'image/png' },
        ],
      });
      navigator.mediaSession.playbackState = 'playing';
    } catch { /* MediaMetadata not available */ }

    const register = (name, handler) => {
      try { navigator.mediaSession.setActionHandler(name, handler || null); }
      catch { /* action unsupported on this browser, skip */ }
    };
    register('play',       onPlay  || null);
    register('pause',      onPause || null);
    register('stop',       onStop  || null);
    register('nexttrack',  onNextTrack || null);
    // Seek forward / back are not meaningful for a live exercise, but
    // we null them explicitly so the OS doesn't paint a disabled button.
    register('seekforward',  null);
    register('seekbackward', null);

    return () => {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
        register('play', null);
        register('pause', null);
        register('stop', null);
        register('nexttrack', null);
      } catch { /* nothing to clean up */ }
      if (silentAudio) {
        try { silentAudio.pause(); silentAudio.src = ''; } catch { /* cleanup */ }
      }
      if (silentUrl) {
        try { URL.revokeObjectURL(silentUrl); } catch { /* cleanup */ }
      }
    };
  }, [active, title, artist, album, artworkUrl, onPlay, onPause, onStop, onNextTrack]);
}

// Minimal-size valid WAV: a ~100 ms mono 8 kHz buffer of silence.
// Emitted as a data URL so we never touch the network. All zeros
// after the 44-byte PCM header, small enough to keep in memory.
function makeSilentWavUrl() {
  const sampleRate = 8000;
  const durationSeconds = 0.1;
  const numSamples = Math.floor(sampleRate * durationSeconds);
  const buffer = new ArrayBuffer(44 + numSamples);
  const view = new DataView(buffer);
  const writeStr = (offset, s) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  // RIFF header.
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + numSamples, true);
  writeStr(8, 'WAVE');
  // fmt chunk.
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);      // PCM chunk size
  view.setUint16(20, 1, true);       // format = PCM
  view.setUint16(22, 1, true);       // channels = mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true); // byte rate
  view.setUint16(32, 1, true);       // block align
  view.setUint16(34, 8, true);       // bits per sample
  // data chunk (all zeros = silence).
  writeStr(36, 'data');
  view.setUint32(40, numSamples, true);
  // Samples already zero-initialized.
  const blob = new Blob([buffer], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}
